import dayjs from 'dayjs'
import { useRouter } from 'next/router'
import React from 'react'

import { IN_DAYS_COUNT_VALUE_LABEL_MAP } from '@/legacy-src/components/filters/timeFilters/constants'
import { isRoutePublicTherapistList } from '@/legacy-src/constants/locale.constants'

import { Button } from '@/src/domains/design-system/components/Button'
import { Heading } from '@/src/domains/design-system/components/Heading'
import { Text } from '@/src/domains/design-system/components/Text'
import { TherapistFilterActive } from '@/src/domains/therapist-filters/components/TherapistFilterActive'
import { TherapistSessionLocationActiveFilter } from '@/src/domains/therapist-filters/components/TherapistSessionLocationActiveFilter'
import { getTherapistSessionTypeFilterName } from '@/src/domains/therapist-filters/utils/getTherapistSessionTypeFilterName'

import useSpecializations from '../../hooks/useSpecializations'
import useTrainings from '../../hooks/useTrainings'
import { getLocalisedLanguageName, getTranslate } from '../../utilities/i18n'
import Icon from '../icons/Icon'
import useInsuranceCompanies from '../insuranceCompanies/hooks/useInsuranceCompanies'
import useFilteredTherapists from '../therapistsList/hooks/useFilteredTherapists'
import {
  FILTER_ALL_SLUGS,
  FILTER_DAYS_SLUG,
  FILTER_IN_DAYS_COUNT,
  FILTER_INSURANCE_SLUG,
  FILTER_LANGUAGE_SLUG,
  FILTER_SEARCH_SLUG,
  FILTER_SESSION_LOCATION_SLUG,
  FILTER_SESSION_TYPE_SLUG,
  FILTER_SPECIALIZATION_SLUG,
  FILTER_TIMES_SLUG,
  FILTER_TRAINING_SLUG,
} from './filters.constants'
import { type FilterSlug } from './filters.interface'
import useFilters from './hooks/useFilters'

type FilterToRemove = { value?: string[]; slug: FilterSlug }

const ActiveFilters: React.FC = () => {
  const { resetAllFilters, hasActiveFilters, getFilterValue, setFilters } = useFilters()
  const { t } = getTranslate()
  const router = useRouter()
  const isPublic = isRoutePublicTherapistList(router.route)
  const { therapists, isLoadingList } = useFilteredTherapists(isPublic)

  const { insuranceCompanies } = useInsuranceCompanies()
  const { specializations, rawSpecializations } = useSpecializations()
  const { trainings } = useTrainings()

  const dataDict = [
    {
      slug: FILTER_INSURANCE_SLUG,
      data: insuranceCompanies,
    },
    {
      slug: FILTER_SPECIALIZATION_SLUG,
      data: rawSpecializations,
    },
    {
      slug: FILTER_TRAINING_SLUG,
      data: trainings,
    },
  ]

  const selectData = (slug: FilterSlug) => {
    return dataDict.find((dataset) => dataset.slug === slug)?.data
  }

  const getName = (id: string, slug: FilterSlug) => {
    if (slug === FILTER_SPECIALIZATION_SLUG) return specializations[id]
    if (slug === FILTER_LANGUAGE_SLUG) return getLocalisedLanguageName(id)
    if (slug === FILTER_DAYS_SLUG)
      return dayjs()
        .day(+id + 1)
        .format('dddd')
    if (slug === FILTER_TIMES_SLUG) return id
    if (slug === FILTER_IN_DAYS_COUNT) {
      return IN_DAYS_COUNT_VALUE_LABEL_MAP[id] ? t(IN_DAYS_COUNT_VALUE_LABEL_MAP[id]) : ''
    }
    if (slug === FILTER_SESSION_TYPE_SLUG) {
      return getTherapistSessionTypeFilterName(id, t)
    }
    const data = selectData(slug) || []
    const entity = data?.find((item) => item.id.toString() === id)
    return entity?.name
  }

  const removeFilterValue = (filters: FilterToRemove[]) => {
    const updatedFilter = filters.map(({ slug, value }) => {
      const allValues = getFilterValue(slug)

      return { slug, value: value ? allValues.filter((v) => !value.includes(v)) : [] }
    })
    setFilters(updatedFilter)
  }

  const hasNoResults = !isLoadingList && therapists.length === 0

  return hasActiveFilters || hasNoResults ? (
    <div className="mb-8 w-full">
      <section className="flex flex-col gap-4 rounded-xl bg-white p-5">
        <header>
          {therapists.length > 0 ? (
            <Heading variant="xl" element="h3" className="m-0">
              {t('components.filter.overview.therapistFoundPlural', { count: therapists.length })}
            </Heading>
          ) : (
            <>
              <Heading variant="xl" element="h3" className="m-0">
                {t('components.filter.overview.noTherapistFound.title')}
              </Heading>
              {hasActiveFilters && (
                <Text variant="lg" element="p" className="m-0">
                  {t('components.filter.overview.noTherapistFound.instructions')}
                </Text>
              )}
            </>
          )}
        </header>
        {hasActiveFilters && (
          <>
            <div className="flex flex-col gap-4">
              {FILTER_ALL_SLUGS.map((slug) => {
                const values = getFilterValue(slug)

                return Boolean(values.length) ? (
                  <div
                    key={slug}
                    className="flex-start flex flex-col gap-1 text-xs tablet:flex-row tablet:flex-wrap tablet:items-center tablet:gap-4"
                  >
                    <Text element="h4" className="m-0">
                      {t(`components.filter.${slug}.title`)}
                    </Text>
                    <ul className="m-0 flex list-none flex-wrap gap-3 p-0">
                      {values.map((id) => {
                        const onRemoveValue = () => {
                          const filters: FilterToRemove[] = [
                            {
                              slug,
                              value: [id],
                            },
                          ]

                          if (slug === FILTER_SESSION_TYPE_SLUG) {
                            filters.push({ slug: FILTER_SESSION_LOCATION_SLUG, value: undefined })
                          }

                          removeFilterValue(filters)
                        }

                        return (
                          <li key={id}>
                            {slug === FILTER_SESSION_LOCATION_SLUG ? (
                              <TherapistSessionLocationActiveFilter
                                id={Number.parseInt(id, 10)}
                                onClick={onRemoveValue}
                              />
                            ) : (
                              <TherapistFilterActive onClick={onRemoveValue}>
                                {slug === FILTER_SEARCH_SLUG ? id : getName(id, slug)}
                              </TherapistFilterActive>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ) : null
              })}
            </div>
            <footer className="flex flex-col items-start gap-4">
              <Button
                onClick={resetAllFilters}
                size="small"
                variant="secondary"
                prefixIcon={<Icon icon="cross" color="primary" />}
              >
                {t('component.button.removeAllFilters')}
              </Button>
              {therapists.length === 1 && (
                <div className="flex items-center rounded-xl border-[1px] border-solid p-3 text-base font-bold text-petrol-500">
                  <Icon icon="info" color="secondary" size="sm" className="mr-3" />
                  {t('components.filter.overview.adjustCriteria')}
                </div>
              )}
            </footer>
          </>
        )}
      </section>
    </div>
  ) : null
}

export default ActiveFilters
